import React, { useState } from "react";
import { convertAmountToPretty, strToFormattedDate } from "../../helpers/formatHelper";
import { useOutletContext } from "react-router-dom";
import CriteriaDisplay from "./components/CriteriaDisplay";
import Card from "../../components/ui/Card";
import { Step } from "../../helpers/stepHelper";
import Button from "../../components/ui/Button";
import WordIcon from "../../assets/icons/word";
import { DocumentType, getDocumentByType } from "../../helpers/documentHelper";
import PdfIcon from "../../assets/icons/pdf";
import DOMPurify from "dompurify";
import ProcurementForm from "./forms/specifications";

export default function Launch() {
  const { procurement, handleUpdateProcurement } = useOutletContext();
  const [showForm, setShowForm] = useState(false);
  const doc = getDocumentByType(procurement.documents, [DocumentType.LAUNCH_NOTE]);

  function SpecificationsButton() {
    if ((procurement.step.name === Step.WRITING_SPECIFICATIONS || procurement.step.name === Step.BUDGET_LEGAL_CHECK) && procurement.isUserAction) {
      return (
        <div className="flex flex-col items-end">
          <Button onClick={() => setShowForm(true)} disabled={procurement.isGenerating}>
            <span>Éditer le cahier des charges et la note de lancement</span>
          </Button>
          {procurement.isGenerating && <span className="text-gray-500 italic text-sm mt-2">Modification en cours de traitement...</span>}
        </div>
      );
    } else {
      return null;
    }
  }

  return (
    <>
      {
        !showForm
          ? (
            <>
              <div className="flex flex-col space-y-6">
                <div className="flex justify-between">
                  <div className="space-y-6">
                    <h2 className="text-2xl font-semibold">Note de lancement</h2>
                    {doc
                      ? (
                        <div
                          className="w-10 h-auto cursor-pointer text-blue-600 flex items-center gap-2"
                          onClick={() => window.open(doc.url, "_blank")}
                        >
                          {doc.name.endsWith(".pdf") ? <PdfIcon /> : <WordIcon />}
                        </div>
                      )
                      : procurement.isGenerating
                        ? (
                          <p className="text-gray-500">
                            Document en cours de génération{" "}
                            <span className="text-sm italic">
                              (veuillez rafraîchir la page d’ici quelques secondes).
                            </span>
                          </p>
                        )
                        : (
                          <p className="text-gray-500">
                            Aucun document{" "}
                            <span className="text-sm italic">
                              (il sera généré après validation du formulaire).
                            </span>
                          </p>
                        )}
                  </div>
                  <SpecificationsButton />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <Card title="Date de la demande" description={strToFormattedDate(procurement.submittedAt)} />
                  <Card title="Date limite de remise des offres" description={strToFormattedDate(procurement.offerLimitAt, "dd/MM/yyyy HH:mm")} />
                  <Card title="Date de fin" description={strToFormattedDate(procurement.endAt, "dd/MM/yyyy")} />

                  <Card title="Date de début de la 1ère tranche" description={strToFormattedDate(procurement.firstTrancheStartAt)} />
                  <Card title="Date de fin de la 1ère tranche" description={strToFormattedDate(procurement.firstTrancheEndAt)} />

                  <Card title="Durée du marché" description={procurement.duration} />
                  {procurement.amountVAT && (
                    <Card
                      title="Montant TVAC"
                      description={`${convertAmountToPretty(procurement.amountVAT)}\u00A0\u00A0\u00A0(${convertAmountToPretty(Math.round(procurement.amountVAT / 1.21))}/HTVA)`}
                    />
                  )}
                  {procurement.dailyRate && (
                    <Card
                      title="Taux journalier TVAC"
                      description={`${convertAmountToPretty(procurement.dailyRate * 1.21)}\u00A0\u00A0\u00A0(${convertAmountToPretty(Math.round(procurement.dailyRate))}/HTVA)`}
                    />
                  )}
                  <Card title="Financement du marché" description={procurement.fundingSource} />
                  <Card title="Sous-traitance" description={procurement.canSubcontracting && "Oui"} />
                  <Card title="Sélection qualitative" description={procurement.hasQualitativeSelection && "Oui"} />
                </div>

                <Card title="Contexte du marché" html={procurement.context} />

                <Card title="Objet du marché" html={procurement.object} />
                {procurement.criteriaSelection && procurement.criteriaSelection.length >= 1 && (
                  <Card title="Critères de sélection">
                    <div className="flex-col space-y-2">
                      {procurement.criteriaSelection.map(({ description }, index) => (
                        <Card key={`criterionSelection-${index}`}>
                          {description && (
                            <>
                              <div
                                className="description-content text-gray-900"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(description),
                                }}
                              />
                            </>
                          )}
                        </Card>
                      ))}
                    </div>
                  </Card>
                )}

                {procurement.criteria && procurement.criteria.length >= 1 && (
                  <Card title="Critères d'attribution">
                    <CriteriaDisplay criteria={procurement.criteria} />
                  </Card>
                )}
                {procurement.services && procurement.services.length >= 1 && (
                  <Card title="Services">
                    <div className="flex-col space-y-2">
                      {procurement.services.map(({ name, description }) => (
                        <Card key={name} title={name}>
                          {description && (
                            <>
                              <p className="text-gray-900">
                                <span className="font-semibold">Description :</span>
                              </p>
                              <div
                                className="description-content text-gray-900"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(description),
                                }}
                              />
                            </>
                          )}
                        </Card>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            </>
          )
          : (
            <div className="h-full relative">
              <div className="absolute right-4">
                <Button
                  onClick={
                    () => setShowForm(false)
                  }
                >
                  <span>Annuler</span>
                </Button>
              </div>
              <ProcurementForm onSave={handleUpdateProcurement} data={procurement} />
            </div>
          )
      }
    </>
  );
}
